/**
 * Token Refresh Service
 *
 * Centralized service for handling token refresh operations.
 * This service can be used by both AuthService and AuthIntegration
 * to avoid code duplication and circular dependencies.
 *
 * @remarks
 * Types have been migrated to the centralized types directory.
 * Import types from '@shared/types/auth' or 'types/auth/token.types'.
 */

import { container, inject, injectable } from 'tsyringe';

import { getAuthBaseUrl } from '../../config/api';
import { TOKENS } from '../../di/container';
import {
  DEFAULT_TOKEN_REFRESH_CONFIG,
  TokenRefreshError,
} from '../../types/auth/token.types';
import { dayjs } from '../../utils/date';
import { timeZoneHeaders } from '../api/timezone';
import '../../utils/string-extensions';

import type { TokenLoginApiResponse } from '../../types/api.types';
import type { AuthToken } from '../../types/auth';
import type { ITokenStorageService } from '../../types/auth/storage.types';
import type {
  TokenRefreshConfig,
  RequiredTokenRefreshConfig,
} from '../../types/auth/token.types';

// Internal type alias for use within this file
type TokenRefreshConfigInternal = TokenRefreshConfig;

// Default config with runtime baseURL
const DEFAULT_CONFIG: RequiredTokenRefreshConfig = {
  ...DEFAULT_TOKEN_REFRESH_CONFIG,
  baseURL: getAuthBaseUrl(),
};

@injectable()
export class TokenRefreshService {
  private config: RequiredTokenRefreshConfig;

  constructor(
    // Optional, and injected by token rather than by type. With no `@inject`
    // the container falls back to the parameter's emitted type, which for an
    // interface is `Object` — and it refuses to build one. Only Babel emits
    // that metadata, so this failed on a device ("TypeInfo not known for
    // Object") and nowhere else: Vitest's esbuild emits none.
    //
    // No default value on purpose: Babel's parameter-decorator support drops a
    // decorator from a parameter that has one, which brings the failure back.
    @inject(TOKENS.TOKEN_REFRESH_CONFIG, { isOptional: true })
    config: TokenRefreshConfigInternal | undefined,
    @inject(TOKENS.TOKEN_STORAGE) private tokenStorage: ITokenStorageService
  ) {
    this.config = { ...DEFAULT_CONFIG, ...(config ?? {}) };
  }

  /**
   * Swap the bearer this device holds for a fresh one.
   *
   * The API refreshes a bearer that is STILL VALID: the request is signed with
   * the access token, and the "refresh token" only names which token to swap.
   * It is the hashid of a row id — not a secret, and never a credential — so
   * once the bearer has expired or been revoked there is nothing left to
   * refresh with, and the only way back is to sign in. Refreshing is therefore
   * something to do ahead of expiry, not in answer to a 401.
   *
   * Every failure is a `TokenRefreshError` whose `reason` says whether the
   * bearer is finished (`rejected`) or may still be good (everything else).
   * This method only reports; deciding what a failure means for the session is
   * `BearerLifecycleService`'s job.
   */
  public async refreshToken(): Promise<AuthToken> {
    const refreshToken = await this.tokenStorage.getRefreshToken();
    if (!refreshToken) {
      throw new TokenRefreshError(
        'no-credentials',
        'No refresh token available'
      );
    }

    const accessToken = await this.tokenStorage.getAccessToken();
    if (!accessToken) {
      throw new TokenRefreshError(
        'no-credentials',
        'No access token to sign the refresh with'
      );
    }

    try {
      const data = await this.makeRefreshRequest(accessToken, refreshToken);

      if (!data.access_token) {
        throw new TokenRefreshError('unrecognised', 'Invalid refresh response');
      }

      // The request was signed with one bearer; if storage holds another by
      // now — a sign-out cleared it, or someone signed in — this answer belongs
      // to a session that is gone. Writing it would bring that session back.
      if ((await this.tokenStorage.getAccessToken()) !== accessToken) {
        throw new TokenRefreshError(
          'superseded',
          'The stored bearer changed during the refresh'
        );
      }

      // Store the new token
      const expiresAt = data.expires_at
        ? dayjs(data.expires_at).valueOf()
        : dayjs().add(data.expires_in, 'seconds').valueOf();

      // Awaited: the caller replays requests the moment this resolves, and
      // they must be signed with the new bearer — the old one is already dead.
      await this.tokenStorage.setTokens(
        data.access_token,
        data.refresh_token || refreshToken,
        expiresAt
      );

      const authToken: AuthToken = {
        access_token: data.access_token,
        token_type: 'Bearer',
        expires_in: data.expires_in, // seconds, as the API sent it
        expires_at: data.expires_at,
        refresh_token: data.refresh_token,
      };

      return authToken;
    } catch (error) {
      // Keeps the reason a typed failure carried; anything untyped — no
      // network, an abort, a storage fault — says nothing about the bearer.
      throw new TokenRefreshError(
        error instanceof TokenRefreshError ? error.reason : 'unavailable',
        `Token refresh failed: ${(error as Error).message}`
      );
    }
  }

  /**
   * Check if a valid refresh token is available
   */
  public async hasValidRefreshToken(): Promise<boolean> {
    return !!(await this.tokenStorage.getRefreshToken());
  }

  /**
   * Get current access token
   */
  public async getCurrentToken(): Promise<string | null> {
    return await this.tokenStorage.getAccessToken();
  }

  /**
   * Check if token is expired or will expire soon
   */
  public async isTokenExpired(thresholdMinutes: number = 5): Promise<boolean> {
    const tokenData = await this.tokenStorage.getTokenData();
    if (!tokenData) return true;

    const now = dayjs().valueOf();
    const thresholdMs = thresholdMinutes * 60 * 1000;
    return tokenData.expiresAt <= now + thresholdMs;
  }

  /**
   * Make the actual refresh request.
   *
   * `PUT /sanctum/token/{refresh}`, signed with the access token. The answer
   * has the shape sign-in has — the token sits under `data.token`, beside
   * `data.user`.
   */
  private async makeRefreshRequest(
    accessToken: string,
    refreshToken: string
  ): Promise<{
    access_token: string;
    refresh_token?: string;
    expires_in: number;
    expires_at?: string;
  }> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

    try {
      const response = await fetch(
        `${this.config.baseURL}${this.config.refreshEndpoint.replaceParam({ token: refreshToken })}`,
        {
          method: 'PUT',
          headers: {
            // Without it Laravel answers an unauthenticated request with a
            // redirect to a login page rather than a 401.
            Accept: 'application/json',
            Authorization: `${this.config.tokenPrefix}${accessToken}`,
            ...timeZoneHeaders(),
          },
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      if (response.status !== 200) {
        // Only a 401 says the bearer is finished. Anything else — a 5xx, a
        // 429 — is the server's trouble, and the bearer may still be good.
        throw new TokenRefreshError(
          response.status === 401 ? 'rejected' : 'unavailable',
          `Refresh failed: ${response.status} ${response.statusText}`
        );
      }

      const body = (await response.json()) as
        Partial<TokenLoginApiResponse> | undefined;
      const token = body?.data?.token;

      // An unknown refresh token is answered 200 with an empty `data`.
      if (!token) {
        throw new TokenRefreshError('unrecognised', 'Invalid refresh response');
      }

      return token;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  /**
   * Update configuration
   */
  public updateConfig(config: Partial<TokenRefreshConfigInternal>): void {
    this.config = { ...this.config, ...config };
  }
}

// Register as singleton and create/get singleton instance
let isRegistered = false;

export const getTokenRefreshService = (): TokenRefreshService => {
  if (!container.isRegistered(TOKENS.TOKEN_STORAGE)) {
    throw new Error(
      'TokenStorageService is not registered. Call registerTokenStorage() first.'
    );
  }

  if (!isRegistered) {
    container.registerSingleton(TokenRefreshService);
    isRegistered = true;
  }
  return container.resolve(TokenRefreshService);
};
